import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext.jsx";
import SocialAuthButtons from "./SocialAuthButtons.jsx";
import SEO from "./SEO.jsx";
import { localizeMessage } from '../utils/toast.js';
import { MARKET } from '../utils/market.js';

// The backend returns plain English error strings (it has no i18n of its own), so a raw
// `result.error` shown as-is reads as untranslated English on an otherwise-localized page.
// This maps the handful of known auth error strings to a translated message; anything not
// listed here still falls through to the raw string rather than being silently swallowed.
const KNOWN_BACKEND_ERROR_KEYS = {
  "Invalid email or password.": "authPages.invalidCredentials",
};

function Registration() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, verifyTwoFactorLogin, socialAuth, user, redirectPath, clearRedirect } = useAuth();
  const navigate = useNavigate();
  const [twoFactorTicket, setTwoFactorTicket] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (redirectPath) {
      clearRedirect();
      navigate(redirectPath);
    }
  }, [redirectPath, navigate, clearRedirect]);

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'secondary_admin' && user.secondaryAdminApproved) {
        navigate('/admin/programs');
      } else {
        navigate('/feed');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.email.trim() || !formData.password) {
      setError(t('authPages.fillRequired'));
      return;
    }
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.requires2FA && result.tempToken) {
        setTwoFactorTicket(result.tempToken);
      } else if (!result.success) {
        if (result.requiresApproval) {
          setError(t('authPages.pendingApproval'));
        } else if (result.data?.code === "EMAIL_NOT_VERIFIED") {
          navigate("/verify-email", { state: { email: result.data?.email || formData.email } });
        } else {
          const knownKey = result.error && KNOWN_BACKEND_ERROR_KEYS[result.error];
          setError(knownKey ? t(knownKey) : (result.error || t('authPages.loginFailed')));
        }
      }
    } catch (err) {
      setError(t('authPages.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await verifyTwoFactorLogin(twoFactorTicket, twoFactorCode);
      if (!result.success) {
        setError(result.error || t('authPages.loginFailed'));
      }
    } catch (err) {
      setError(t('authPages.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async ({ provider, accessToken }) => {
    setError("");
    setLoading(true);
    try {
      // This is the unified login page (reachable from anywhere, not the org-specific
      // /reg flow) — it never asks which account type the person is, so a brand-new
      // social sign-up here must default to a regular player account, not organizer.
      const result = await socialAuth({
        provider,
        accessToken,
        mode: "register",
        userType: "player",
        market: MARKET,
        // Send new sign-ups through the same username/name/age confirmation + interests
        // step as the email-registration flow and PlayerReg.jsx's own social buttons
        // already do, instead of straight to /home — skipping it meant a social sign-up
        // never got a username/date of birth, never picked interests, and (since the
        // onboarding tour only auto-arms after this step sets its localStorage flags)
        // never saw the guided tour either.
        redirectTo: "/player-interests",
      });

      if (!result.success) {
        setError(result.error || t('authPages.socialRegisterFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );

  const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  if (twoFactorTicket) {
    return (
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f7f5', padding: 20 }}>
        <form onSubmit={handleTwoFactorSubmit} style={{ background: 'white', borderRadius: 16, padding: 32, width: '100%', maxWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1a3d2e' }}>Two-factor code</h2>
          <p style={{ margin: 0, fontSize: 13, color: '#888' }}>Enter the code from your authenticator app, or a backup code.</p>
          <input
            autoFocus
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value.trim())}
            placeholder="123456"
            style={{ padding: '12px 14px', border: '1.5px solid #dde4de', borderRadius: 9, fontSize: 16, letterSpacing: 2, textAlign: 'center', fontFamily: 'inherit' }}
          />
          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{localizeMessage(error)}</div>}
          <button type="submit" disabled={loading || !twoFactorCode} style={{ padding: '12px 16px', background: '#32614d', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {loading ? '...' : 'Verify'}
          </button>
          <button type="button" onClick={() => { setTwoFactorTicket(null); setTwoFactorCode(''); setError(''); }} style={{ background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer' }}>
            Back to login
          </button>
        </form>
      </section>
    );
  }

  /* ══ MOBILE ══ */
  if (isMobile) {
    return (
      <section style={{
        position: 'fixed',
        inset: 0,
        background: '#f5f7f0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 20px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}>
        <SEO
          titleKey="pages.seo.registration.title"
          descriptionKey="pages.seo.registration.description"
          keywordsKey="pages.seo.registration.keywords"
          path="/registration"
          breadcrumbLabel="Sign Up"
        />
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '28px 20px 24px',
          width: '100%',
          maxWidth: '360px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#2b2b52', textAlign: 'center', margin: '0 0 18px' }}>{t('authPages.login')}</h2>

          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>{t('authPages.email')}</label>
              <input
                type="email"
                placeholder="your@email.com"
                required
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: '10px',
                  border: '1.5px solid #ddd', fontSize: '14px', outline: 'none',
                  boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#32614d'}
                onBlur={e => e.target.style.borderColor = '#ddd'}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>{t('authPages.password')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t('authPages.password')}
                  required
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  style={{
                    width: '100%', padding: '11px 42px 11px 14px', borderRadius: '10px',
                    border: '1.5px solid #ddd', fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box', transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#32614d'}
                  onBlur={e => e.target.style.borderColor = '#ddd'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: '12px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', padding: 0, color: '#666',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <Link to="/forgot-password" style={{ alignSelf: 'flex-end', fontSize: '12.5px', color: '#32614d', fontWeight: 600, textDecoration: 'none' }}>{t('authPages.forgotPassword')}</Link>
            </div>

            {error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: '10px', fontSize: '12px', border: '1px solid #ffcdd2' }}>
                {localizeMessage(error)}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px', borderRadius: '10px',
                background: loading ? '#ccc' : 'linear-gradient(135deg, #32614d, #2a5240)',
                color: 'white', border: 'none', fontSize: '15px',
                fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                marginTop: '4px', transition: 'opacity 0.2s',
              }}
            >
              {loading ? t('authPages.loggingIn') : t('authPages.continueBtn')}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#8a8a8a', fontSize: 11 }}>
              <div style={{ flex: 1, height: 1, background: '#e6ece8' }} />
              <span>{t('authPages.or')}</span>
              <div style={{ flex: 1, height: 1, background: '#e6ece8' }} />
            </div>

            <SocialAuthButtons onAuth={handleSocialLogin} disabled={loading} compact mode="register" />

            <p style={{ textAlign: 'center', fontSize: '13px', color: '#666', margin: 0 }}>
              {t('authPages.dontHaveAccount')}{' '}
              <Link to="/playerReg" style={{ color: '#32614d', fontWeight: 600, textDecoration: 'none' }}>{t('authPages.registerAsUser')}</Link>
              {' '}·{' '}
              <Link to="/reg" style={{ color: '#32614d', fontWeight: 600, textDecoration: 'none' }}>{t('authPages.registerAsOrg')}</Link>
            </p>
          </form>
        </div>
      </section>
    );
  }

  /* ══ DESKTOP — original ══ */
  return (
    <section className="registration-page">
      <SEO
        titleKey="pages.seo.registration.title"
        descriptionKey="pages.seo.registration.description"
        keywordsKey="pages.seo.registration.keywords"
        path="/registration"
        breadcrumbLabel="Sign Up"
      />
      <div className="image-side">
        <img src="/nkarner/registration-visual.png" alt={t('authPages.registrationVisual')} />
      </div>

      <div className="form-side">
        <h2>{t('authPages.login')}</h2>
        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <div className="formm-group">
            <label>{t('authPages.email')}</label>
            <input
              type="email"
              placeholder="your@email.com"
              required
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div className="formm-group">
            <label>{t('authPages.password')}</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t('authPages.password')}
                required
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('authPages.hidePassword') : t('authPages.showPassword')}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            <Link to="/forgot-password" style={{ display: 'block', textAlign: 'right', marginTop: 6, fontSize: '12.5px', color: '#32614d', fontWeight: 600, textDecoration: 'none' }}>{t('authPages.forgotPassword')}</Link>
          </div>

          {error && <div className="error-message">{localizeMessage(error)}</div>}

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? t('authPages.loggingIn') : t('authPages.continueBtn')}
          </button>

          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#8a8a8a', fontSize: 11 }}>
              <div style={{ flex: 1, height: 1, background: '#e6ece8' }} />
              <span>{t('authPages.or')}</span>
              <div style={{ flex: 1, height: 1, background: '#e6ece8' }} />
            </div>
            <SocialAuthButtons onAuth={handleSocialLogin} disabled={loading} mode="register" />
          </div>

          <p className="register-text">
            {t('authPages.dontHaveAccount')} <Link to="/playerReg">{t('authPages.registerAsUser')}</Link> · <Link to="/reg">{t('authPages.registerAsOrg')}</Link>
          </p>
        </form>
      </div>

      <style>{`
        .password-input-container { position: relative; width: 100%; }
        .password-input-container input { width: 100%; padding-right: 45px; }
        .password-toggle-btn { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; padding: 5px; color: #666; display: flex; align-items: center; justify-content: center; transition: color 0.3s ease; }
        .password-toggle-btn:hover { color: #333; }
        .password-toggle-btn:focus { outline: none; }
      `}</style>
    </section>
  );
}

export default Registration;
